import axios from "axios";
import { resolveApiBase } from "../utils/apiBase";
import {
  BUSINESS_SESSION_KEY,
  SHOP_TOKEN_KEY,
  refreshAccessToken,
} from "./sessionRenewal";

/** =========================================================
 *  Env + Config
 *  ========================================================= */
const API_BASE = resolveApiBase();

/**
 * Resolve any avatar/image value coming from the backend into an absolute URL
 * that works in every environment.
 *
 * Handles three storage shapes:
 *  1. Already-absolute URL  → "https://…" or "http://…"  → return as-is
 *  2. Root-relative path    → "/images/avatars/…"         → prefix with API_BASE
 *  3. Bare filename         → "avatar-1-123.jpg"          → prefix with API_BASE/images/
 */
export const getImageUrl = (filename) => {
  if (!filename) return "";
  // Shape 1 – fully-qualified URL (local or cloud)
  if (/^https?:\/\//.test(filename)) return filename;
  // Shape 2 – root-relative path already containing the directory
  if (filename.startsWith("/")) return `${API_BASE}${filename}`;
  // Shape 3 – bare filename
  return `${API_BASE}/images/${filename}`;
};

function resolveAssetAuthToken(portal = "shop") {
  const normalizedPortal = String(portal || "shop").trim().toLowerCase();

  if (normalizedPortal === "business" || normalizedPortal === "seller" || normalizedPortal === "admin") {
    return getBusinessToken() || getShopToken() || null;
  }

  if (normalizedPortal === "auto") {
    return getBusinessToken() || getShopToken() || null;
  }

  return getShopToken() || getBusinessToken() || null;
}

export const getAuthenticatedAssetUrl = (relativePath, options = {}) => {
  if (!relativePath) return "";
  const normalizedPath = String(relativePath).trim();
  if (!normalizedPath) return "";

  const isAbsolute = /^https?:\/\//i.test(normalizedPath);
  const base = isAbsolute
    ? normalizedPath
    : `${API_BASE}${normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`}`;

  const isApiAsset =
    !isAbsolute ||
    (API_BASE && base.startsWith(API_BASE));
  if (!isApiAsset) {
    return base;
  }

  if (/(?:[?&])token=/.test(base)) {
    return base;
  }

  const token = options?.token || resolveAssetAuthToken(options?.portal);
  if (!token) return base;

  const joiner = base.includes("?") ? "&" : "?";
  return `${base}${joiner}token=${encodeURIComponent(token)}`;
};
const API_DEBUG = String(process.env.REACT_APP_API_DEBUG || "").toLowerCase() === "true";
const API_TIMEOUT = Number(process.env.REACT_APP_API_TIMEOUT || 15000);

/** =========================================================
 *  Route Registry
 *  =========================================================
 * GOAL (Business Portal):
 * - Keep frontend “backend-ready”
 * - Frontend does NOT decide permissions
 * - Backend returns:
 *   - 401 (not logged in)
 *   - 403 (no permission)
 *
 * IMPORTANT:
 * Your older file mixed "/admin/..." and "/api/admin/...".
 * This rewrite standardizes to "/api/..." to avoid common 404s.
 *
 * If your backend still uses "/admin/..." temporarily,
 * change here once — and the whole app follows.
 */
export const ROUTES = {
  /** ---------------- SHOP ---------------- */
  products: "/api/products",
  productFacets: "/api/products/facets",
  productById: (id) => `/api/products/${id}`,
  productReviewLike: (productId, reviewId) => `/api/products/${productId}/reviews/${reviewId}/likes`,
  cart: "/api/cart",
  cartAdd: "/api/cart/add",
  cartUpdate: "/api/cart/update",
  cartUpdateVariant: "/api/cart/update-variant",
  cartRemove: "/api/cart/remove",
  cartClear: "/api/cart/clear",
  cartMerge: "/api/cart/merge",
  orders: "/api/orders",
  userWallet: "/api/users/me/wallet",
  userWalletCashIns: "/api/users/me/wallet/cash-ins",
  userWalletCashInById: (id) => `/api/users/me/wallet/cash-ins/${id}`,
  userAddresses: "/api/users/addresses",
  userAddressById: (id) => `/api/users/addresses/${id}`,
  orderById: (id) => `/api/orders/${id}`,

  /** -------- SHOP AUTH -------- */
  authLogin: "/api/auth/login",
  authRegister: "/api/auth/register",
  authVerifyEmailOtp: "/api/auth/verify-email-otp",
  authResendEmailOtp: "/api/auth/resend-email-otp",
  authMe: "/api/auth/me",
  authLogout: "/api/auth/logout",

  /** -------- BUSINESS PORTAL AUTH --------
   * If your backend uses shared auth: keep /api/auth/login
   * If it uses dedicated business login: set to /api/business/login
   */
  businessAuthLogin: "/api/auth/login",
  businessAuthMe: "/api/auth/me",
  businessAuthLogout: "/api/auth/logout",

  /** -------- BUSINESS PORTAL (Owner/Manager/Staff) --------
   * Backend should enforce role & permission using:
   * - business_members + business_roles + business_role_permissions
   */
  businessDashboard: "/api/business/dashboard",

  businessProducts: "/api/business/products",
  businessProductReviews: "/api/business/products/reviews",
  businessProductReviewReply: (reviewId) => `/api/business/products/reviews/${reviewId}/reply`,
  businessProductById: (id) => `/api/business/products/${id}`,

  businessProductDraftCreate: "/api/business/products/drafts",
  businessProductDraftUpdate: (id) => `/api/business/products/drafts/${id}`,
  businessProductDraftPublish: (id) => `/api/business/products/drafts/${id}/publish`,
  businessProductStatus: (id) => `/api/business/products/${id}/status`,
  businessProductVariant3D: (id) => `/api/business/products/${id}/variants/3d`,
  businessProductUploadImage: "/api/business/products/upload",
  assetUploadInit: "/api/assets/uploads/init",
  assetUploadFinalize: "/api/assets/uploads/finalize",

  businessMembers: "/api/business/members",
  businessMemberById: (id) => `/api/business/members/${id}`,
  businessMemberRole: (id) => `/api/business/members/${id}/role`,
  businessMemberProfile: (id) => `/api/business/members/${id}/profile`,
  businessMemberEmploymentTypes: "/api/business/members/hr/employment-types",

  /** -------- BUSINESS INVITATIONS (for invited users) -------- */
  businessInvitationsPending: "/api/businesses/invitations/pending",
  businessInvitationDecline: (id) => `/api/businesses/invitations/${id}/decline`,
  accountBusinessOnboardings: "/api/businesses/onboarding",
  accountBusinessOnboardingById: (membershipId) => `/api/businesses/onboarding/${membershipId}`,

  businessOrders: "/api/business/orders",
  businessOrderById: (id) => `/api/business/orders/${id}`,
  businessOrderStatus: (id) => `/api/business/orders/${id}/status`,
  businessOrderReturnRequestApprove: (id) => `/api/business/orders/${id}/return-request/approve`,
  businessOrderReturnRequestReject: (id) => `/api/business/orders/${id}/return-request/reject`,
  businessOrderReturnRequestRefundWallet: (id) => `/api/business/orders/${id}/return-request/refund-wallet`,

  businessProductSales: "/api/business/products/reports/sales",

  /** -------- BUSINESS ANALYTICS -------- */
  businessAnalytics: "/api/business/analytics",

  /** -------- BUSINESS CATEGORIES -------- */
  businessCategories: "/api/business/products/categories",
  businessCategoryById: (id) => `/api/business/products/categories/${id}`,
  businessTags: "/api/business/products/tags",
  businessTagById: (id) => `/api/business/products/tags/${id}`,

  /** -------- BUSINESS DASHBOARD -------- */
  businessDashboardSummary: "/api/business/products/dashboard/summary",
  businessDashboardRecentProducts: "/api/business/products/dashboard/recent",
  businessDashboardInventoryHealth: "/api/business/products/dashboard/inventory-health",
  businessDashboardTopSelling: "/api/business/products/dashboard/top-selling",

  /** -------- SUPPLIERS -------- */
  businessSuppliers: "/api/business/suppliers",
  businessSupplierById: (id) => `/api/business/suppliers/${id}`,
  businessSupplierProducts: (id) => `/api/business/suppliers/${id}/products`,
  businessSupplierLinkProduct: (id) => `/api/business/suppliers/${id}/products`,
  businessSupplierUnlinkProduct: (id, pid) => `/api/business/suppliers/${id}/products/${pid}`,
  businessSupplierByCode: "/api/business/suppliers/by-code",
  businessProductSuppliers: (productId) => `/api/business/suppliers/product-suppliers/${productId}`,

  /** -------- SUPPLIER PORTAL -------- */
  supplierRegister: "/api/supplier/auth/register",
  supplierLogin: "/api/supplier/auth/login",
  supplierVerifyEmail: "/api/supplier/auth/verify-email",
  supplierResendOtp: "/api/supplier/auth/resend-otp",
  supplierMe: "/api/supplier/auth/me",
  supplierRFQs: "/api/supplier/rfqs",
  supplierRFQById: (id) => `/api/supplier/rfqs/${id}`,
  supplierSubmitQuote: (rfqId) => `/api/supplier/rfqs/${rfqId}/quote`,
  supplierPOs: "/api/supplier/orders",
  supplierPOById: (id) => `/api/supplier/orders/${id}`,
  supplierAcknowledgePO: (id) => `/api/supplier/orders/${id}/acknowledge`,
  supplierDeclineRFQ: (rfqId) => `/api/supplier/rfqs/${rfqId}/decline`,
  supplierDeclinePO: (id) => `/api/supplier/orders/${id}/decline`,
  supplierCreateDeliveryForPO: (id) => `/api/supplier/orders/${id}/deliveries`,
  supplierDeliveries: "/api/supplier/deliveries",
  supplierDeliveryById: (id) => `/api/supplier/deliveries/${id}`,

  // Supplier settings & notifications
  supplierNotifications: "/api/supplier/notifications",
  supplierNotificationRead: (id) => `/api/supplier/notifications/${id}/read`,
  supplierUnreadCount: "/api/supplier/notifications/unread-count",
  supplierSettingsProfile: "/api/supplier/settings/profile",
  supplierSettingsNotifications: "/api/supplier/settings/notifications",
  supplierSettingsNotificationPrefs: "/api/supplier/settings/notifications/preferences",
  supplierSettingsNotificationGlobal: "/api/supplier/settings/notifications/global",
  supplierInviteCodes: "/api/supplier/invite-codes",
  supplierInviteCodeById: (id) => `/api/supplier/invite-codes/${id}`,

  // Supplier portal: businesses, products, dashboard
  supplierBusinesses: "/api/supplier/businesses",
  supplierBusinessById: (id) => `/api/supplier/businesses/${id}`,
  supplierProducts: "/api/supplier/products",
  supplierDashboardSummary: "/api/supplier/dashboard/summary",

  /** -------- PROCUREMENT -------- */
  businessPRs: "/api/business/procurement/requisitions",
  businessPRApprove: (id) => `/api/business/procurement/requisitions/${id}/approve`,
  businessPRCancel: (id) => `/api/business/procurement/requisitions/${id}/cancel`,
  businessRFQs: "/api/business/procurement/rfqs",
  businessRFQById: (id) => `/api/business/procurement/rfqs/${id}`,
  businessRFQInvite: (id) => `/api/business/procurement/rfqs/${id}/invite`,
  businessRFQQuotes: (id) => `/api/business/procurement/rfqs/${id}/quotes`,
  businessRFQAcceptQuote: (rfqId, quoteId) => `/api/business/procurement/rfqs/${rfqId}/quotes/${quoteId}/accept`,
  businessPOs: "/api/business/procurement/orders",
  businessPOById: (id) => `/api/business/procurement/orders/${id}`,
  businessPOSend: (id) => `/api/business/procurement/orders/${id}/send`,
  businessProcurementTimeline: "/api/business/procurement/timeline",
  businessProcurementOpenWork: "/api/business/procurement/open-work",

  /** -------- INVENTORY -------- */
  businessInventoryReceive: "/api/business/inventory/receive",
  businessInventoryAdjust: "/api/business/inventory/adjust",
  businessInventoryMovements: "/api/business/inventory/movements",
  businessInventoryReceipts: "/api/business/inventory/receipts",
  businessInventoryLevels: "/api/business/inventory/levels",

  /** -------- FINANCE -------- */
  businessFinanceDashboard: "/api/business/finance/dashboard",
  businessFinancePending: "/api/business/finance/pending",
  businessFinancePayables: "/api/business/finance/payables",
  businessFinanceDisbursements: "/api/business/finance/disbursements",
  businessFinanceSalaryTemplates: "/api/business/finance/salary-templates",
  businessFinanceSalaryTemplateById: (id) => `/api/business/finance/salary-templates/${id}`,
  businessFinanceReceivables: "/api/business/finance/receivables",
  businessFinanceCollections: "/api/business/finance/collections",
  businessFinancePayroll: "/api/business/finance/payroll",
  businessFinancePayrollReviewApprove: (periodId) => `/api/business/finance/payroll/${periodId}/review-approve`,
  businessFinancePayrollReturn: (periodId) => `/api/business/finance/payroll/${periodId}/return`,
  businessFinancePayrollSettle: (periodId) => `/api/business/finance/payroll/${periodId}/settle`,
  businessFinancePayrollSalaries: (periodId) => `/api/business/finance/payroll/${periodId}/salaries`,
  businessFinancePayrollCalculations: (periodId) => `/api/business/finance/payroll/${periodId}/calculations`,
  businessFinanceSalaryCashHandover: (recordId) => `/api/business/finance/payroll/salaries/${recordId}/cash-handover`,
  businessFinanceSalaryConfirmOnBehalf: (recordId) => `/api/business/finance/payroll/salaries/${recordId}/confirm-on-behalf`,
  businessFinanceAnalytics: "/api/business/finance/analytics",
  businessFinanceDisbursementRelease: "/api/business/finance/disbursements/release",
  businessFinanceCollectionRecord: "/api/business/finance/collections/record",
  businessFinanceApprove: (id) => `/api/business/finance/orders/${id}/approve`,
  businessFinanceReject: (id) => `/api/business/finance/orders/${id}/reject`,

  /** -------- LOGISTICS -------- */
  businessDeliveries: "/api/business/logistics",
  businessDeliveryById: (id) => `/api/business/logistics/${id}`,
  businessDeliveryStatus: (id) => `/api/business/logistics/${id}/status`,

  /** -------- HR MODULE -------- */
  businessHRDepartments: "/api/business/hr/departments",
  businessHRDepartmentById: (id) => `/api/business/hr/departments/${id}`,
  businessHRDepartmentMembers: (id) => `/api/business/hr/departments/${id}/members`,
  businessHRShifts: "/api/business/hr/shifts",
  businessHRShiftById: (id) => `/api/business/hr/shifts/${id}`,
  businessHRShiftAssign: (id) => `/api/business/hr/shifts/${id}/assign`,
  businessHRShiftAssignments: "/api/business/hr/shifts/assignments",
  businessHRShiftAssignableMembers: "/api/business/hr/shifts/assignable-members",
  businessHRShiftAssignmentReassign: (id) => `/api/business/hr/shifts/assignments/${id}/reassign`,
  businessHRShiftAssignmentById: (id) => `/api/business/hr/shifts/assignments/${id}`,
  businessHRShiftOverrides: "/api/business/hr/shifts/overrides",
  businessHRShiftStaffing: "/api/business/hr/shifts/staffing",
  businessHRShiftOverrideById: (id) => `/api/business/hr/shifts/overrides/${id}`,
  businessHRMemberSchedule: (memberId) => `/api/business/hr/shifts/schedule/member/${memberId}`,
  businessHRMySchedule: "/api/business/hr/shifts/schedule/me",
  businessHRAttendance: "/api/business/hr/attendance",
  businessHRAttendanceSummary: "/api/business/hr/attendance/summary",
  businessHRAttendanceCheckIn: "/api/business/hr/attendance/check-in",
  businessHRAttendanceCheckOut: "/api/business/hr/attendance/check-out",
  businessHRAttendanceManual: "/api/business/hr/attendance/manual",
  businessHRAttendanceById: (id) => `/api/business/hr/attendance/${id}`,
  businessHRAttendanceEmployee: (memberId) => `/api/business/hr/attendance/employee/${memberId}`,
  businessHRAttendanceCorrections: "/api/business/hr/attendance/corrections",
  businessHRAttendanceCorrectionDecision: (id, decision) => `/api/business/hr/attendance/corrections/${id}/${decision}`,
  businessHRScheduleRequests: "/api/business/hr/shifts/requests",
  businessHRScheduleRequestDecision: (id, decision) => `/api/business/hr/shifts/requests/${id}/${decision}`,
  businessMyHROverview: "/api/business/hr/me/overview",
  businessMyHRDeductions: "/api/business/hr/me/deductions",
  businessMyHRAttendance: "/api/business/hr/me/attendance",
  businessMyHRRequests: "/api/business/hr/me/requests",
  businessMyHRShiftOptions: "/api/business/hr/me/shift-options",
  businessMyHRCorrections: "/api/business/hr/me/attendance-corrections",
  businessMyHRCorrectionCancel: (id) => `/api/business/hr/me/attendance-corrections/${id}/cancel`,
  businessMyHRScheduleRequests: "/api/business/hr/me/schedule-requests",
  businessMyHRScheduleRequestCancel: (id) => `/api/business/hr/me/schedule-requests/${id}/cancel`,
  businessMyHRLeave: "/api/business/hr/me/leave",
  businessMyHRLeavePreview: "/api/business/hr/me/leave/preview",
  businessMyHRLeaveRequests: "/api/business/hr/me/leave-requests",
  businessMyHRLeaveRequestCancel: (id) => `/api/business/hr/me/leave-requests/${id}/cancel`,
  businessMyHROvertime: "/api/business/hr/me/overtime",
  businessMyHROvertimeCancel: (id) => `/api/business/hr/me/overtime/${id}/cancel`,
  businessHROvertime: "/api/business/hr/attendance/overtime",
  businessHROvertimeDecision: (id, decision) => `/api/business/hr/attendance/overtime/${id}/${decision}`,
  businessHRScheduledOvertime: "/api/business/hr/attendance/overtime/scheduled",
  businessHRScheduledOvertimeAction: (id, action) => `/api/business/hr/attendance/overtime/scheduled/${id}/${action}`,
  businessHRScheduledOvertimeException: (id, date) => `/api/business/hr/attendance/overtime/scheduled/${id}/exceptions/${date}`,
  businessHRScheduledOvertimeDays: (memberId) => `/api/business/hr/attendance/overtime/scheduled/member/${memberId}/days`,
  businessHRScheduledOvertimeSchedules: (memberId) => `/api/business/hr/attendance/overtime/scheduled/member/${memberId}/schedules`,
  businessHROvertimeReviewItems: "/api/business/hr/attendance/overtime/review-items",
  businessMyHRScheduledOvertime: "/api/business/hr/me/overtime/scheduled",
  businessMyHROvertimeReviewItems: "/api/business/hr/me/overtime/review-items",
  businessHRScheduleFit: (memberId) => `/api/business/hr/compensation/${memberId}/schedule-fit`,
  businessHRScheduleFitPreview: "/api/business/hr/compensation/schedule-fit/preview",
  businessHRScheduleFitProposed: (memberId) => `/api/business/hr/compensation/${memberId}/schedule-fit/proposed`,
  businessHRSeparations: "/api/business/hr/separations",
  businessHRMemberSeparation: (memberId) => `/api/business/hr/separations/members/${memberId}`,
  businessHREndOnboarding: (memberId) => `/api/business/hr/separations/members/${memberId}/end-onboarding`,
  businessHRSeparationAction: (id, action) => `/api/business/hr/separations/${id}/${action}`,
  businessHRCompensation: (memberId) => `/api/business/hr/compensation/${memberId}`,
  businessHRCompensationCurrent: (memberId) => `/api/business/hr/compensation/${memberId}/current`,
  businessHRMyCompensation: "/api/business/hr/compensation/me/current",
  // Statutory foundations (CD-S1): government rules, government numbers, business registrations, statutory facts.
  businessHRStatutoryRules: "/api/business/hr/statutory/rules",
  businessHRMyGovernmentNumbers: "/api/business/hr/statutory/me/government-numbers",
  businessHRMyGovernmentNumberReveal: "/api/business/hr/statutory/me/government-numbers/reveal",
  businessHRBusinessRegistrations: "/api/business/hr/statutory/business-registrations",
  businessHRBusinessRegistrationReveal: "/api/business/hr/statutory/business-registrations/reveal",
  businessHREmployeeGovernmentNumbers: (memberId) => `/api/business/hr/statutory/employees/${memberId}/government-numbers`,
  businessHREmployeeGovernmentNumberReveal: (memberId) => `/api/business/hr/statutory/employees/${memberId}/government-numbers/reveal`,
  businessHREmployeeStatutoryFacts: (memberId) => `/api/business/hr/statutory/employees/${memberId}/facts`,
  businessHREmployeeStatutoryFactCancel: (memberId, factId) => `/api/business/hr/statutory/employees/${memberId}/facts/${factId}/cancel`,
  businessHRCompensationById: (memberId, compId) => `/api/business/hr/compensation/${memberId}/${compId}`,
  businessHRSettings: "/api/business/hr/settings",
  businessHRCalendarYear: (year) => `/api/business/hr/calendar/years/${year}`,
  businessHRCalendarClosures: "/api/business/hr/calendar/closures",
  businessHRCalendarClosureById: (id) => `/api/business/hr/calendar/closures/${id}`,
  businessHRHolidayPayHandling: (memberId) => `/api/business/hr/calendar/employees/${memberId}/holiday-pay-handling`,
  businessHRLeaveTypes: "/api/business/hr/leave/types",
  businessHRLeaveTypeById: (id) => `/api/business/hr/leave/types/${id}`,
  businessHRLeaveBalances: (memberId) => `/api/business/hr/leave/balances/${memberId}`,
  businessHRLeaveBalance: (memberId, typeId) => `/api/business/hr/leave/balances/${memberId}/types/${typeId}`,
  businessHRLeaveBalanceInit: (memberId) => `/api/business/hr/leave/balances/${memberId}/initialize`,
  businessHRLeaveCarryOver: "/api/business/hr/leave/carry-over",
  businessHRLeaveRequests: "/api/business/hr/leave/requests",
  businessHRLeaveRequestById: (id) => `/api/business/hr/leave/requests/${id}`,
  businessHRDeductionTypes: "/api/business/hr/deductions/types",
  businessHRDeductionTypeById: (id) => `/api/business/hr/deductions/types/${id}`,
  businessHREmployeeDeductions: (memberId) => `/api/business/hr/deductions/employee/${memberId}`,
  businessHREmployeeDeductionById: (id) => `/api/business/hr/deductions/employee/assignments/${id}`,
  businessHRPayrollSchedules: "/api/business/hr/payroll/schedules",
  businessHRPayrollPeriods: "/api/business/hr/payroll/periods",
  businessHRPayrollMissingPeriods: "/api/business/hr/payroll/periods/missing",
  businessHRPaySchedules: "/api/business/hr/payroll/pay-schedules",
  businessHRPaySchedule: (id) => `/api/business/hr/payroll/pay-schedules/${id}`,
  businessHRPayAssignmentsBulk: "/api/business/hr/payroll/pay-assignments/bulk",
  businessHRPayAssignments: "/api/business/hr/payroll/pay-assignments",
  businessHRPayAssignmentsUnassigned: "/api/business/hr/payroll/pay-assignments/unassigned",
  businessHRPayAssignmentsTransfer: "/api/business/hr/payroll/pay-assignments/transfer",
  businessHRPayAssignmentCancel: (assignmentId) => `/api/business/hr/payroll/pay-assignments/${assignmentId}/cancel`,
  businessHRMemberPaySchedule: (memberId) => `/api/business/hr/payroll/pay-assignments/member/${memberId}`,
  businessHRMemberPayTransferOptions: (memberId) => `/api/business/hr/payroll/pay-assignments/member/${memberId}/transfer-options`,
  businessHRPayrollPeriodById: (id) => `/api/business/hr/payroll/periods/${id}`,
  businessHRPayrollRelease: (id) => `/api/business/hr/payroll/periods/${id}/release`,
  businessHRPayrollResults: (periodId) => `/api/business/hr/payroll/periods/${periodId}/results`,
  businessHRPayrollResultById: (id) => `/api/business/hr/payroll/results/${id}`,
  businessHRPayrollMemberResults: (memberId) => `/api/business/hr/payroll/results/member/${memberId}`,
  businessHRPayrollPayslips: (periodId) => `/api/business/hr/payroll/periods/${periodId}/payslips`,
  businessHRPayrollMemberPayslips: (memberId) => `/api/business/hr/payroll/payslips/member/${memberId}`,
  businessHRPayrollPayslipById: (id) => `/api/business/hr/payroll/payslips/${id}`,
  businessHRPayrollMyPayslips: "/api/business/hr/payroll/me/payslips",
  businessHRPayrollMyPayslipDetails: (id) => `/api/business/hr/payroll/me/payslips/${id}/details`,
  businessHRFinancialRecords: "/api/business/hr/financial-records",
  businessHRMemberFinancialRecords: (memberId) => `/api/business/hr/financial-records/member/${memberId}`,
  businessHRAnalytics: "/api/business/hr/analytics",
  businessHROnboardingById: (memberId) => `/api/business/hr/onboarding/${memberId}`,

  /** -------- BUSINESS APPLICATION (Shop user applies) -------- */
  businessApply: "/api/businesses/apply",
  businessApplicationDocument: (applicationId, documentId) =>
    `/api/businesses/applications/${applicationId}/documents/${documentId}`,

  /** -------- ADMIN (System level) -------- */
  adminDashboard: "/api/admin/dashboard",
  adminStats: "/api/admin/dashboard-stats",
  adminUsers: "/api/admin/users",
  adminBusinesses: "/api/admin/businesses",
  adminBusinessApplications: "/api/admin/applications",
  adminBusinessApplicationCounts: "/api/admin/applications/counts",
  adminBusinessApplicationById: (id) => `/api/admin/applications/${id}`,
  adminBusinessApplicationHistory: (id) => `/api/admin/applications/${id}/history`,
  adminBusinessApplicationDocument: (applicationId, documentId) =>
    `/api/admin/applications/${applicationId}/documents/${documentId}`,
  adminBusinessApplicationApprove: (id) => `/api/admin/applications/${id}/approve`,
  adminBusinessApplicationReject: (id) => `/api/admin/applications/${id}/reject`,
  adminBusinessApplicationRequestInfo: (id) => `/api/admin/applications/${id}/request-info`,
  adminBusinessById: (id) => `/api/admin/businesses/${id}`,
  adminBusinessModerate: (id) => `/api/admin/businesses/${id}/moderate`,
  adminBusinessTimeline: (id) => `/api/admin/businesses/${id}/timeline`,
  adminCommissionRevenue: "/api/admin/commission-revenue",
  adminCommissionRate: "/api/admin/commission-rate",

  // Legacy endpoints (old flow via businesses table directly)
  adminPendingBusinesses: "/api/admin/pending-businesses",
  adminPendingBusinessApprove: (id) => `/api/admin/pending-businesses/${id}/approve`,
  adminPendingBusinessReject: (id) => `/api/admin/pending-businesses/${id}/reject`,
};


/** =========================================================
 *  Debug helper
 *  ========================================================= */
function debugLog(...args) {
  if (API_DEBUG) console.log("[api]", ...args);
}

/** =========================================================
 *  Session token helpers
 *  =========================================================
 * - SHOP token (normal customer)
 * - BUSINESS session token (Owner/Manager/Staff portal)
 *
 * NOTE:
 * Some of your older code stored "admin_session".
 * We keep backward compatibility by checking it too.
 */
const LEGACY_ADMIN_SESSION_KEY = "admin_session";

function getShopToken() {
  try {
    return localStorage.getItem(SHOP_TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

function getBusinessToken() {
  try {
    const raw =
      localStorage.getItem(BUSINESS_SESSION_KEY) ||
      localStorage.getItem(LEGACY_ADMIN_SESSION_KEY);

    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.token || null;
  } catch {
    return null;
  }
}

/** =========================================================
 *  Axios Client
 *  =========================================================
 * withCredentials:
 * - TRUE if backend uses cookies/session
 * - FALSE if backend is pure Bearer token only
 *
 * Keep it TRUE for now (safer when backend changes).
 */
export const apiClient = axios.create({
  baseURL: API_BASE || undefined,
  timeout: API_TIMEOUT,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

/** =========================================================
 *  Request Interceptor
 *  =========================================================
 * Rules:
 * - If request is FormData → DO NOT force Content-Type
 * - Attach token depending on request type:
 *   - business endpoints → business token
 *   - otherwise → shop token
 *
 * Optional:
 * - You can force business mode via header: X-Portal: "business"
 */
apiClient.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};

    const url = String(config.url || "");
    // Normalize to a request path so checks work with absolute or relative URLs
    const requestPath = (() => {
      try {
        // If url is absolute, extract the pathname
        const maybe = new URL(url, config.baseURL || API_BASE);
        return maybe.pathname + (maybe.search || "");
      } catch {
        return url;
      }
    })();

    const isFormData =
      typeof FormData !== "undefined" && config.data instanceof FormData;

    if (isFormData) {
      // Let browser set multipart boundary automatically
      if (config.headers["Content-Type"]) delete config.headers["Content-Type"];
    } else {
      config.headers["Content-Type"] =
        config.headers["Content-Type"] || "application/json";
    }

    const forcedPortal = String(
      config.headers["X-Portal"] || config.headers["x-portal"] || ""
    ).toLowerCase();

    const isSupplierRequest = forcedPortal === "supplier" || requestPath.startsWith("/api/supplier/");
    const isBusinessRequest =
      forcedPortal === "shop"
        ? false
        : forcedPortal === "business" ||
          requestPath.startsWith("/api/messaging/businesses/") ||
          (requestPath.startsWith("/api/business") && !requestPath.includes("/apply") && !requestPath.includes("/invitations/")) ||
          requestPath.startsWith("/business");

    let supplierToken = null;
    if (isSupplierRequest) {
      try { supplierToken = JSON.parse(localStorage.getItem("supplier_session") || "null")?.token || null; } catch { /* no session */ }
      config.headers["X-Portal"] = "supplier";
    }
    // Public Careers calls (forcedPortal "guest") never carry a session token.
    const token = forcedPortal === "guest" ? null : isSupplierRequest ? supplierToken : isBusinessRequest ? getBusinessToken() : getShopToken();
    config.__portalScope = isSupplierRequest ? "supplier" : isBusinessRequest ? "business" : "shop";
    if (token) config.headers.Authorization = `Bearer ${token}`;

    debugLog(
      "request",
      (config.method || "GET").toUpperCase(),
      `${config.baseURL || ""}${config.url || ""}`,
      config.__portalScope,
      config.params ? { params: config.params } : ""
    );

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * When an access token expires, get another and carry on.
 *
 * Access tokens are short-lived by design, so expiry is routine rather than a
 * fault - it should cost the person nothing. A 401 triggers one refresh and one
 * retry; if the refresh fails the session really is over and the original 401
 * stands, so existing sign-out handling still runs.
 *
 * Only ONE retry, tracked on the request itself: if the retried request 401s
 * again, the token is not the problem and looping would only hammer the server.
 */
const AUTH_PATHS = ["/api/auth/login", "/api/auth/refresh", "/api/auth/logout", "/api/supplier/auth/login", "/api/supplier/auth/logout"];

function isAuthEndpoint(config) {
  const url = String(config?.url || "");
  return AUTH_PATHS.some((path) => url.includes(path));
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;
    const shouldRetry = error?.response?.status === 401
      && config
      && !config.__retriedAfterRefresh
      && !isAuthEndpoint(config);

    if (!shouldRetry) return Promise.reject(error);

    // Keyed on the token that failed: if the portal has already moved to a
    // newer session, this failure is stale and must not disturb it.
    const failedToken = String(config.headers?.Authorization || "").replace(/^Bearer /, "") || null;
    const token = await refreshAccessToken(config.__portalScope, { failedToken });
    if (!token) return Promise.reject(error);

    config.__retriedAfterRefresh = true;
    config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
    return apiClient(config);
  },
);

/** =========================================================
 *  Error Helpers
 *  ========================================================= */
function pickMessageFromResponse(data) {
  if (!data) return null;
  if (typeof data === "string") return data;
  if (typeof data?.message === "string") return data.message;
  if (typeof data?.msg === "string") return data.msg;
  if (typeof data?.error === "string") return data.error;
  if (typeof data?.detail === "string") return data.detail;

  if (typeof data?.errors === "string") return data.errors;
  if (Array.isArray(data?.errors) && data.errors.length) return String(data.errors[0]);

  if (data?.errors && typeof data.errors === "object") {
    const firstKey = Object.keys(data.errors)[0];
    if (firstKey) return String(data.errors[firstKey]);
  }

  return null;
}

function toErrorMessage(error) {
  if (axios.isCancel?.(error)) return "Request cancelled";
  if (error?.code === "ERR_CANCELED") return "Request cancelled";

  if (error?.response) {
    const status = error.response.status;
    const msgFromApi = pickMessageFromResponse(error.response.data);
    return `Request failed: ${status}${msgFromApi ? ` — ${msgFromApi}` : ""}`;
  }

  if (error?.message) return `Request failed: ${error.message}`;
  return "Request failed: Unknown error";
}

/** =========================================================
 *  Central Request Wrapper
 *  ========================================================= */
export async function request(config) {
  try {
    const res = await apiClient(config);
    return res.data;
  } catch (error) {
    const msg = toErrorMessage(error);
    debugLog("error", msg);

    const status = error?.response?.status || null;
    const isCanceled = axios.isCancel?.(error) || error?.code === "ERR_CANCELED";

    const portalScope = error?.config?.__portalScope || "shop";

    // Only clear the business session for business-scoped unauthenticated requests.
    if (status === 401 && portalScope === "business") {
      try {
        localStorage.removeItem(BUSINESS_SESSION_KEY);
        localStorage.removeItem(LEGACY_ADMIN_SESSION_KEY);
      } catch {
        // ignore
      }
    }

    // ✅ throw an enriched error so callers can detect cancel / status
    const e = new Error(msg);
    e.status = status;
    e.response = error?.response;
    e.code = error?.code;
    e.isCanceled = isCanceled;
    e.portalScope = portalScope;
    // The server's own message and code, without the "Request failed: NNN"
    // prefix, for screens that show errors to people. `message` is unchanged
    // because some screens still parse it.
    e.serverMessage = pickMessageFromResponse(error?.response?.data) || null;
    e.serverCode = error?.response?.data?.code || null;
    e.serverDetails = error?.response?.data?.errors || null;
    throw e;
  }
}

/** =========================================================
 *  Response Normalizers
 *  ========================================================= */
function normalizeListResponse(payload) {
  if (Array.isArray(payload)) return { items: payload, meta: null };
  if (Array.isArray(payload?.data)) return { items: payload.data, meta: payload?.meta || null };
  if (Array.isArray(payload?.data?.items))
    return { items: payload.data.items, meta: payload.data.meta || payload.meta || null };
  if (Array.isArray(payload?.items)) return { items: payload.items, meta: payload.meta || null };
  return { items: [], meta: null };
}

function normalizeSingleResponse(payload) {
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    if (payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
      return payload.data;
    }
    return payload;
  }
  return null;
}

/** ======================================================================
 *                              SHOP API
 * ====================================================================== */

// Products
export async function fetchProducts(options = {}) {
  const params = {};
  if (options.category) params.category = options.category;
  if (options.business) params.business = options.business;
  if (options.limit) params.limit = options.limit;
  if (options.page) params.page = options.page;
  if (options.q) params.q = options.q;
  if (options.sort) params.sort = options.sort;
  if (Array.isArray(options.colors) && options.colors.length > 0) {
    params.colors = options.colors.join(",");
  }
  if (Array.isArray(options.sizes) && options.sizes.length > 0) {
    params.sizes = options.sizes.join(",");
  }
  if (Number.isFinite(Number(options.minRating)) && Number(options.minRating) > 0) {
    params.minRating = Number(options.minRating);
  }
  if (Number.isFinite(Number(options.maxRating)) && Number(options.maxRating) > 0) {
    params.maxRating = Number(options.maxRating);
  }
  if (Number.isFinite(Number(options.minPrice)) && Number(options.minPrice) >= 0) {
    params.minPrice = Number(options.minPrice);
  }
  if (Number.isFinite(Number(options.maxPrice)) && Number(options.maxPrice) >= 0) {
    params.maxPrice = Number(options.maxPrice);
  }

  const data = await request({
    url: ROUTES.products,
    method: "GET",
    params,
    signal: options.signal,
  });

  return normalizeListResponse(data);
}

export async function searchSuggestions(query, limit = 6) {
  const normalizedQuery = String(query || "").trim();
  const data = await fetchProducts({
    q: normalizedQuery,
    limit,
    sort: normalizedQuery ? "relevance" : "popular",
  });
  const items = Array.isArray(data?.items) ? data.items : [];
  return items.map((item) => ({
    ...item,
    id: item.id ?? item._id,
    name: item.name ?? item.title ?? "Untitled",
  }));
}

export async function fetchProduct(id, options = {}) {
  if (!id) throw new Error("Request failed: Missing product id");
  const data = await request({
    url: ROUTES.productById(id),
    method: "GET",
    signal: options.signal,
  });
  return normalizeSingleResponse(data);
}

export async function fetchProductReviews(productId, options = {}) {
  const data = await request({
    url: `${ROUTES.productById(productId)}/reviews`,
    method: "GET",
    signal: options.signal,
  });
  return data;
}

export async function submitProductReview(productId, payload, options = {}) {
  const data = await request({
    url: `${ROUTES.productById(productId)}/reviews`,
    method: "POST",
    data: payload,
    signal: options.signal,
  });
  return data;
}

export async function likeProductReview(productId, reviewId, options = {}) {
  if (!productId) throw new Error("Request failed: Missing product id");
  if (!reviewId) throw new Error("Request failed: Missing review id");

  return request({
    url: ROUTES.productReviewLike(productId, reviewId),
    method: "POST",
    signal: options.signal,
  });
}

export async function unlikeProductReview(productId, reviewId, options = {}) {
  if (!productId) throw new Error("Request failed: Missing product id");
  if (!reviewId) throw new Error("Request failed: Missing review id");

  return request({
    url: ROUTES.productReviewLike(productId, reviewId),
    method: "DELETE",
    signal: options.signal,
  });
}

/**
 * Fetch product categories from the backend.
 * @returns {Promise<Array<{id, name, slug, product_count}>>}
 */
export async function fetchCategories(options = {}) {
  const data = await request({
    url: `${ROUTES.products}/categories`,
    method: "GET",
    signal: options.signal,
  });
  return data?.data || data || [];
}

export async function fetchProductFacets(options = {}) {
  const params = {};
  if (options.category) params.category = options.category;
  if (options.q) params.q = options.q;
  if (Array.isArray(options.colors) && options.colors.length > 0) {
    params.colors = options.colors.join(",");
  }
  if (Array.isArray(options.sizes) && options.sizes.length > 0) {
    params.sizes = options.sizes.join(",");
  }
  if (Number.isFinite(Number(options.minRating)) && Number(options.minRating) > 0) {
    params.minRating = Number(options.minRating);
  }
  if (Number.isFinite(Number(options.maxRating)) && Number(options.maxRating) > 0) {
    params.maxRating = Number(options.maxRating);
  }
  if (Number.isFinite(Number(options.minPrice)) && Number(options.minPrice) >= 0) {
    params.minPrice = Number(options.minPrice);
  }
  if (Number.isFinite(Number(options.maxPrice)) && Number(options.maxPrice) >= 0) {
    params.maxPrice = Number(options.maxPrice);
  }
  if (options.business) params.business = options.business;

  const data = await request({
    url: ROUTES.productFacets,
    method: "GET",
    params,
    signal: options.signal,
  });

  return data?.data || data || { colors: [], sizes: [], ratings: [] };
}

// Auth (shop)
export async function login(credentials, options = {}) {
  return request({
    url: ROUTES.authLogin,
    method: "POST",
    data: credentials,
    signal: options.signal,
  });
}

export async function register(payload, options = {}) {
  return request({
    url: ROUTES.authRegister,
    method: "POST",
    data: payload,
    signal: options.signal,
  });
}

export async function verifyEmailOtp(payload, options = {}) {
  return request({
    url: ROUTES.authVerifyEmailOtp,
    method: "POST",
    data: payload,
    signal: options.signal,
  });
}

export async function resendEmailOtp(payload, options = {}) {
  return request({
    url: ROUTES.authResendEmailOtp,
    method: "POST",
    data: payload,
    signal: options.signal,
  });
}

// Cart
export async function fetchCart(options = {}) {
  return request({ url: ROUTES.cart, method: "GET", signal: options.signal });
}

export async function addToCart(payload, options = {}) {
  const productId = payload?.productId ?? payload?.id ?? payload?._id;
  if (!productId) throw new Error("Request failed: Missing product id");
  return request({
    url: ROUTES.cartAdd,
    method: "POST",
    data: {
      productId,
      variantId: payload?.variantId ?? null,
      quantity: payload?.quantity ?? 1,
    },
    signal: options.signal,
  });
}

export async function updateCartItem(payload, options = {}) {
  // scope-based bulk operations (all / business) don't require an item reference
  const isScopeOp = payload?.scope === "all" || payload?.scope === "business";
  if (!isScopeOp && !payload?.itemId && !payload?.productId) {
    throw new Error("Request failed: Missing cart item reference");
  }
  return request({
    url: ROUTES.cartUpdate,
    method: "PUT",
    data: payload,
    signal: options.signal,
  });
}

export async function removeCartItem(payload, options = {}) {
  if (!payload?.itemId && !payload?.productId) {
    throw new Error("Request failed: Missing cart item reference");
  }
  return request({
    url: ROUTES.cartRemove,
    method: "DELETE",
    data: payload,
    signal: options.signal,
  });
}

export async function clearCart(options = {}) {
  return request({ url: ROUTES.cartClear, method: "DELETE", signal: options.signal });
}

export async function mergeCart(items = [], options = {}) {
  return request({
    url: ROUTES.cartMerge,
    method: "POST",
    data: { items },
    signal: options.signal,
  });
}

export const cartApi = {
  fetchCart,
  addToCart,
  updateCartItem,
  removeCartItem,
  clearCart,
  mergeCart,
};

export async function updateCartVariant(payload, options = {}) {
  if (!payload?.itemId || !payload?.newVariantId) {
    throw new Error("Request failed: Missing item or variant reference");
  }
  return request({
    url: ROUTES.cartUpdateVariant,
    method: "PUT",
    data: payload,
    signal: options.signal,
  });
}

// Orders
export async function createOrder(order, options = {}) {
  return request({
    url: ROUTES.orders,
    method: "POST",
    data: order,
    signal: options.signal,
  });
}

export const orderApi = { createOrder };

/**
 * Fetch recommended products based on product IDs in cart/order.
 * @param {number[]} productIds - Array of product IDs
 * @param {number} limit - Max number of recommendations
 */
export async function fetchRecommendations(productIds = [], limit = 8) {
  const ids = productIds.join(',');
  if (!ids) return [];
  const data = await request({
    url: `${ROUTES.products}/recommendations`,
    params: { ids, limit },
  });
  const items = data?.data || [];
  return items.map((item) => ({
    ...item,
    price: item.price || (item.sale_price_cents
      ? item.sale_price_cents / 100
      : item.base_price_cents
        ? item.base_price_cents / 100
        : 0),
  }));
}

/**
 * Check if the current user is eligible to review a product.
 * Requires authentication.
 */
export async function checkReviewEligibility(productId, options = {}) {
  const params = {};
  if (options.orderId != null) params.orderId = options.orderId;
  if (options.orderItemId != null) params.orderItemId = options.orderItemId;

  const data = await request({
    url: `${ROUTES.productById(productId)}/reviews/eligibility`,
    params,
    signal: options.signal,
  });
  return data;
}

/**
 * Fetch a single order by ID.
 */
export async function fetchOrder(orderId, options = {}) {
  const data = await request({
    url: ROUTES.orderById(orderId),
    signal: options.signal,
  });
  return data?.data || data;
}

export async function fetchMyWallet(options = {}) {
  const data = await request({
    url: ROUTES.userWallet,
    method: "GET",
    params: {
      page: options.page,
      limit: options.limit,
    },
    signal: options.signal,
  });

  return data?.data || data;
}

export async function createWalletCashInSession(payload, options = {}) {
  const data = await request({
    url: ROUTES.userWalletCashIns,
    method: "POST",
    data: payload,
    signal: options.signal,
  });

  return data?.data || data;
}

export async function fetchWalletCashIn(cashInId, options = {}) {
  if (!cashInId) throw new Error("Request failed: Missing wallet cash-in id");

  const data = await request({
    url: ROUTES.userWalletCashInById(cashInId),
    method: "GET",
    signal: options.signal,
  });

  return data?.data || data;
}

// Addresses
export async function fetchAddresses(options = {}) {
  const data = await request({ url: ROUTES.userAddresses, method: "GET", signal: options.signal });
  return data?.data || data || [];
}

export async function createAddress(payload, options = {}) {
  const data = await request({ url: ROUTES.userAddresses, method: "POST", data: payload, signal: options.signal });
  return data?.data || data;
}

export async function updateAddressApi(id, payload, options = {}) {
  const data = await request({ url: ROUTES.userAddressById(id), method: "PUT", data: payload, signal: options.signal });
  return data?.data || data;
}

export async function deleteAddress(id, options = {}) {
  return request({ url: ROUTES.userAddressById(id), method: "DELETE", signal: options.signal });
}

export async function setDefaultAddress(id, options = {}) {
  const data = await request({ url: `${ROUTES.userAddressById(id)}/default`, method: "PATCH", signal: options.signal });
  return data?.data || data;
}

export const addressApi = { 
  fetchAddresses, 
  createAddress, 
  updateAddress: updateAddressApi, 
  deleteAddress,
  setDefaultAddress
};

/** ======================================================================
 *                     BUSINESS APPLICATION API (Shop user)
 * ====================================================================== */
export async function applyForBusiness(payload, options = {}) {
  const fd = new FormData();

  Object.entries(payload || {}).forEach(([key, value]) => {
    if (key === "files" || key === "additionalDocs") return;
    if (typeof value === "undefined" || value === null) return;
    if (key === "documentAssets") {
      fd.append(key, JSON.stringify(value));
      return;
    }
    fd.append(key, String(value));
  });

  const files = payload?.files || {};
  if (files.permit) fd.append("permit", files.permit);
  if (files.idFront) fd.append("idFront", files.idFront);
  if (files.idBack) fd.append("idBack", files.idBack);

  // Append multiple additional documents
  const additionalDocs = payload?.additionalDocs || [];
  for (const file of additionalDocs) {
    fd.append("additional_documents", file);
  }

  return request({
    url: ROUTES.businessApply,
    method: "POST",
    data: fd,
    signal: options.signal,
  });
}

export function getBusinessApplicationDocumentUrl(applicationId, documentId) {
  return getAuthenticatedAssetUrl(ROUTES.businessApplicationDocument(applicationId, documentId));
}

/** ======================================================================
 *                     ACCOUNT-SIDE BUSINESS ONBOARDING API
 * ====================================================================== */
export const accountBusinessApi = {
  listOnboardings: (options = {}) =>
    request({
      url: ROUTES.accountBusinessOnboardings,
      method: "GET",
      headers: { "X-Portal": "shop" },
      signal: options.signal,
    }),

  getOnboarding: (membershipId, options = {}) =>
    request({
      url: ROUTES.accountBusinessOnboardingById(membershipId),
      method: "GET",
      headers: { "X-Portal": "shop" },
      signal: options.signal,
    }),


};

/** ======================================================================
 *                    BUSINESS PORTAL API (Owner/Manager/Staff)
 * ====================================================================== */
export const businessApi = {
  /** -------- Auth -------- */
  businessLogin: (credentials, options = {}) =>
    request({
      url: ROUTES.businessAuthLogin,
      method: "POST",
      data: credentials,
      // ensures interceptor treats it as business even if backend uses shared /api/auth/login
      headers: { "X-Portal": "business" },
      signal: options.signal,
    }),

  businessLogout: (options = {}) =>
    request({
      url: ROUTES.businessAuthLogout,
      method: "POST",
      headers: { "X-Portal": "business" },
      signal: options.signal,
    }),

  /** -------- Dashboard -------- */
  getDashboard: (options = {}) =>
    request({
      url: ROUTES.businessDashboard,
      method: "GET",
      headers: { "X-Portal": "business" },
      signal: options.signal,
    }),

  /** -------- Products CRUD -------- */
  getProducts: (options = {}) =>
    request({
      url: ROUTES.businessProducts,
      method: "GET",
      headers: { "X-Portal": "business" },
      signal: options.signal,
    }),

  getProduct: (id, options = {}) => {
    if (!id) throw new Error("Request failed: Missing product id");
    return request({
      url: ROUTES.businessProductById(id),
      method: "GET",
      headers: { "X-Portal": "business" },
      signal: options.signal,
    });
  },

  createProduct: (payload, options = {}) =>
    request({
      url: ROUTES.businessProducts,
      method: "POST",
      data: payload,
      headers: { "X-Portal": "business" },
      signal: options.signal,
    }),

  updateProduct: (id, payload, options = {}) => {
    if (!id) throw new Error("Request failed: Missing product id");
    return request({
      url: ROUTES.businessProductById(id),
      method: "PUT",
      data: payload,
      headers: { "X-Portal": "business" },
      signal: options.signal,
    });
  },

  deleteProduct: (id, options = {}) => {
    if (!id) throw new Error("Request failed: Missing product id");
    return request({
      url: ROUTES.businessProductById(id),
      method: "DELETE",
      headers: { "X-Portal": "business" },
      signal: options.signal,
    });
  },

  /** -------- Product Wizard Draft Flow -------- */
  createProductDraft: (payload = {}, options = {}) =>
    request({
      url: ROUTES.businessProductDraftCreate,
      method: "POST",
      data: payload,
      headers: { "X-Portal": "business" },
      signal: options.signal,
    }),

  updateProductDraft: (draftId, payload, options = {}) => {
    if (!draftId) throw new Error("Request failed: Missing draft id");
    return request({
      url: ROUTES.businessProductDraftUpdate(draftId),
      method: "PATCH",
      data: payload,
      headers: { "X-Portal": "business" },
      signal: options.signal,
    });
  },

  publishProductDraft: (draftId, payload, options = {}) => {
    if (!draftId) throw new Error("Request failed: Missing draft id");
    return request({
      url: ROUTES.businessProductDraftPublish(draftId),
      method: "POST",
      data: payload,
      headers: { "X-Portal": "business" },
      signal: options.signal,
    });
  },

  uploadProductImage: (file, meta = {}, options = {}) => {
    if (!file) throw new Error("Request failed: Missing file");
    const fd = new FormData();
    fd.append("file", file);
    if (meta.type) fd.append("type", meta.type);
    if (meta.variantKey) fd.append("variantKey", meta.variantKey);

    return request({
      url: ROUTES.businessProductUploadImage,
      method: "POST",
      data: fd,
      headers: { "X-Portal": "business" },
      signal: options.signal,
    });
  },

  /** -------- Members (Owner controls staff) -------- */
  getMembers: (options = {}) =>
    request({
      url: ROUTES.businessMembers,
      method: "GET",
      headers: { "X-Portal": "business" },
      signal: options.signal,
    }),

  updateMemberRole: (id, role, options = {}) => {
    if (!id) throw new Error("Request failed: Missing member id");
    return request({
      url: ROUTES.businessMemberRole(id),
      method: "PATCH",
      data: { role },
      headers: { "X-Portal": "business" },
      signal: options.signal,
    });
  },

  updateMemberProfile: (id, data, options = {}) => {
    if (!id) throw new Error("Request failed: Missing member id");
    return request({
      url: ROUTES.businessMemberProfile(id),
      method: "PATCH",
      data,
      headers: { "X-Portal": "business" },
      signal: options.signal,
    });
  },

  removeMember: (id, options = {}) => {
    if (!id) throw new Error("Request failed: Missing member id");
    return request({
      url: ROUTES.businessMemberById(id),
      method: "DELETE",
      headers: { "X-Portal": "business" },
      signal: options.signal,
    });
  },

  /** -------- HR: Member Actions -------- */
  suspendMember: (id, reason, options = {}) => {
    if (!id) throw new Error("Request failed: Missing member id");
    return request({ url: ROUTES.businessMemberById(id) + "/suspend", method: "PATCH",
      data: { reason }, headers: { "X-Portal": "business" }, signal: options.signal });
  },

  reinstateMember: (id, reason = null, options = {}) => {
    if (!id) throw new Error("Request failed: Missing member id");
    return request({ url: ROUTES.businessMemberById(id) + "/reinstate", method: "PATCH",
      data: reason ? { reason } : {},
      headers: { "X-Portal": "business" }, signal: options.signal });
  },


  updateMemberNotes: (id, notes, options = {}) => {
    if (!id) throw new Error("Request failed: Missing member id");
    return request({ url: ROUTES.businessMemberById(id) + "/notes", method: "PATCH",
      data: { notes }, headers: { "X-Portal": "business" }, signal: options.signal });
  },

  getMemberActivity: (id, options = {}) => {
    if (!id) throw new Error("Request failed: Missing member id");
    return request({ url: ROUTES.businessMemberById(id) + "/activity", method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal });
  },

  getBusinessActivityLogs: (params = {}, options = {}) =>
    request({ url: ROUTES.businessMembers + "/activity", method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- HR: Aggregated Views -------- */
  getHRActivityLogs: (params = {}, options = {}) =>
    request({ url: ROUTES.businessMembers + "/hr/activity", method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getHRRoles: (options = {}) =>
    request({ url: ROUTES.businessMembers + "/hr/roles", method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  getHRPermissions: (options = {}) =>
    request({ url: ROUTES.businessMembers + "/hr/permissions", method: "GET",
      params: options.roleId ? { roleId: options.roleId } : {},
      headers: { "X-Portal": "business" }, signal: options.signal }),

  updateRolePermissions: (roleId, permissionIds, options = {}) =>
    request({ url: ROUTES.businessMembers + `/hr/roles/${roleId}/permissions`, method: "PUT",
      data: { permissionIds }, headers: { "X-Portal": "business" }, signal: options.signal }),

  createRole: (data, options = {}) =>
    request({ url: ROUTES.businessMembers + "/hr/roles", method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  cloneRole: (roleId, data, options = {}) =>
    request({ url: ROUTES.businessMembers + `/hr/roles/${roleId}/clone`, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  updateRoleMeta: (roleId, data, options = {}) =>
    request({ url: ROUTES.businessMembers + `/hr/roles/${roleId}`, method: "PATCH",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  deleteRole: (roleId, options = {}) =>
    request({ url: ROUTES.businessMembers + `/hr/roles/${roleId}`, method: "DELETE",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  getEmploymentRecords: (options = {}) =>
    request({ url: ROUTES.businessMembers + "/hr/records", method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  getEmploymentTypes: (options = {}) =>
    request({ url: ROUTES.businessMemberEmploymentTypes, method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  /** Returns action types filtered by category slug. Pass no category for all. */
  getHRActionTypes: (category = null, options = {}) =>
    request({ url: ROUTES.businessMembers + "/hr/action-types", method: "GET",
      params: category ? { category } : {},
      headers: { "X-Portal": "business" }, signal: options.signal }),

  /** Employment history timeline (event-sourced) */
  getEmploymentHistory: (params = {}, options = {}) =>
    request({ url: ROUTES.businessMembers + "/hr/history", method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- HR: Departments -------- */
  getDepartments: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRDepartments, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getDepartment: (id, options = {}) =>
    request({ url: ROUTES.businessHRDepartmentById(id), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  createDepartment: (data, options = {}) =>
    request({ url: ROUTES.businessHRDepartments, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  updateDepartment: (id, data, options = {}) =>
    request({ url: ROUTES.businessHRDepartmentById(id), method: "PATCH",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  deactivateDepartment: (id, options = {}) =>
    request({ url: ROUTES.businessHRDepartmentById(id), method: "DELETE",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  // APP-HR-080: restore an archived department; delete one that was never used (the server proves it).
  restoreDepartment: (id, options = {}) =>
    request({ url: `${ROUTES.businessHRDepartmentById(id)}/restore`, method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  deleteDepartment: (id, options = {}) =>
    request({ url: `${ROUTES.businessHRDepartmentById(id)}/permanent`, method: "DELETE",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  getDepartmentMembers: (id, options = {}) =>
    request({ url: ROUTES.businessHRDepartmentMembers(id), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- HR: Shifts -------- */
  getShifts: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRShifts, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getShift: (id, options = {}) =>
    request({ url: ROUTES.businessHRShiftById(id), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  createShift: (data, options = {}) =>
    request({ url: ROUTES.businessHRShifts, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  updateShift: (id, data, options = {}) =>
    request({ url: ROUTES.businessHRShiftById(id), method: "PATCH",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  deactivateShift: (id, options = {}) =>
    request({ url: ROUTES.businessHRShiftById(id), method: "DELETE",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  // APP-HR-080: restore an archived shift; delete one nothing has ever used (the server proves it).
  restoreShift: (id, options = {}) =>
    request({ url: `${ROUTES.businessHRShiftById(id)}/restore`, method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  deleteShift: (id, options = {}) =>
    request({ url: `${ROUTES.businessHRShiftById(id)}/permanent`, method: "DELETE",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  getShiftAssignments: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRShiftAssignments, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getShiftAssignableMembers: (options = {}) =>
    request({ url: ROUTES.businessHRShiftAssignableMembers, method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  assignShift: (shiftId, data, options = {}) =>
    request({ url: ROUTES.businessHRShiftAssign(shiftId), method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  reassignShiftAssignment: (assignmentId, data, options = {}) =>
    request({ url: ROUTES.businessHRShiftAssignmentReassign(assignmentId), method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  unassignShift: (assignmentId, options = {}) =>
    request({ url: ROUTES.businessHRShiftAssignmentById(assignmentId), method: "DELETE",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  getShiftStaffing: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRShiftStaffing, method: "GET", params,
      headers: { "X-Portal": "business" }, signal: options.signal }),
  getShiftOverrides: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRShiftOverrides, method: "GET", params,
      headers: { "X-Portal": "business" }, signal: options.signal }),
  setShiftOverride: (data, options = {}) =>
    request({ url: ROUTES.businessHRShiftOverrides, method: "POST", data,
      headers: { "X-Portal": "business" }, signal: options.signal }),
  removeShiftOverride: (overrideId, options = {}) =>
    request({ url: ROUTES.businessHRShiftOverrideById(overrideId), method: "DELETE",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  getMemberSchedule: (memberId, params = {}, options = {}) =>
    request({ url: ROUTES.businessHRMemberSchedule(memberId), method: "GET", params,
      headers: { "X-Portal": "business" }, signal: options.signal }),
  getMySchedule: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRMySchedule, method: "GET", params,
      headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- HR: Attendance -------- */
  getAttendanceRecords: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRAttendance, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getAttendanceSummary: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRAttendanceSummary, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  attendanceCheckIn: (data, options = {}) =>
    request({ url: ROUTES.businessHRAttendanceCheckIn, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  attendanceCheckOut: (data, options = {}) =>
    request({ url: ROUTES.businessHRAttendanceCheckOut, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  attendanceManualEntry: (data, options = {}) =>
    request({ url: ROUTES.businessHRAttendanceManual, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  editAttendance: (id, data, options = {}) =>
    request({ url: ROUTES.businessHRAttendanceById(id), method: "PATCH",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  // Report 70 final safeguards: HR closes an abandoned clock-in as "clock-out missing" (no time is invented).
  markAttendanceClockOutMissing: (id, data, options = {}) =>
    request({ url: `${ROUTES.businessHRAttendanceById(id)}/mark-missing-clock-out`, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  deleteAttendance: (id, data = {}, options = {}) =>
    request({ url: ROUTES.businessHRAttendanceById(id), method: "DELETE",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  getEmployeeAttendance: (memberId, params = {}, options = {}) =>
    request({ url: ROUTES.businessHRAttendanceEmployee(memberId), method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- HR: Requests review (Batch 3) -------- */
  getAttendanceCorrections: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRAttendanceCorrections, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),
  decideAttendanceCorrection: (id, decision, data = {}, options = {}) =>
    request({ url: ROUTES.businessHRAttendanceCorrectionDecision(id, decision), method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),
  getScheduleRequests: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRScheduleRequests, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),
  decideScheduleRequest: (id, decision, data = {}, options = {}) =>
    request({ url: ROUTES.businessHRScheduleRequestDecision(id, decision), method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- My HR (own data; the server resolves the member) -------- */
  getMyHROverview: (options = {}) =>
    request({ url: ROUTES.businessMyHROverview, method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  getMyAttendanceDays: (params = {}, options = {}) =>
    request({ url: ROUTES.businessMyHRAttendance, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),
  getMyRequests: (options = {}) =>
    request({ url: ROUTES.businessMyHRRequests, method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  getMyShiftOptions: (options = {}) =>
    request({ url: ROUTES.businessMyHRShiftOptions, method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  submitAttendanceCorrection: (data, options = {}) =>
    request({ url: ROUTES.businessMyHRCorrections, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),
  cancelAttendanceCorrection: (id, options = {}) =>
    request({ url: ROUTES.businessMyHRCorrectionCancel(id), method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  submitScheduleRequest: (data, options = {}) =>
    request({ url: ROUTES.businessMyHRScheduleRequests, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),
  cancelScheduleRequest: (id, options = {}) =>
    request({ url: ROUTES.businessMyHRScheduleRequestCancel(id), method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  getMyLeave: (params = {}, options = {}) =>
    request({ url: ROUTES.businessMyHRLeave, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),
  previewMyLeave: (params = {}, options = {}) =>
    request({ url: ROUTES.businessMyHRLeavePreview, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),
  submitMyLeave: (data, options = {}) =>
    request({ url: ROUTES.businessMyHRLeaveRequests, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),
  cancelMyLeave: (id, options = {}) =>
    request({ url: ROUTES.businessMyHRLeaveRequestCancel(id), method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  getMyOvertime: (options = {}) =>
    request({ url: ROUTES.businessMyHROvertime, method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  requestOvertime: (data, options = {}) =>
    request({ url: ROUTES.businessMyHROvertime, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),
  cancelMyOvertime: (id, options = {}) =>
    request({ url: ROUTES.businessMyHROvertimeCancel(id), method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- HR: Overtime review (Batch 4) -------- */
  getOvertimeRequests: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHROvertime, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),
  assignOvertime: (data, options = {}) =>
    request({ url: ROUTES.businessHROvertime, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),
  decideOvertime: (id, decision, data = {}, options = {}) =>
    request({ url: ROUTES.businessHROvertimeDecision(id, decision), method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- HR: Scheduled overtime and overtime review (Report 70) -------- */
  getScheduledOvertime: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRScheduledOvertime, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),
  proposeScheduledOvertime: (data, options = {}) =>
    request({ url: ROUTES.businessHRScheduledOvertime, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),
  actOnScheduledOvertime: (id, action, data = {}, options = {}) =>
    request({ url: ROUTES.businessHRScheduledOvertimeAction(id, action), method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),
  removeScheduledOvertimeException: (id, date, options = {}) =>
    request({ url: ROUTES.businessHRScheduledOvertimeException(id, date), method: "DELETE",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  getScheduledOvertimeDays: (memberId, params = {}, options = {}) =>
    request({ url: ROUTES.businessHRScheduledOvertimeDays(memberId), method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),
  getScheduledOvertimeSchedules: (memberId, options = {}) =>
    request({ url: ROUTES.businessHRScheduledOvertimeSchedules(memberId), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  getOvertimeReviewItems: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHROvertimeReviewItems, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),
  recordOvertimeReview: (data, options = {}) =>
    request({ url: ROUTES.businessHROvertimeReviewItems, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),
  getMyScheduledOvertime: (options = {}) =>
    request({ url: ROUTES.businessMyHRScheduledOvertime, method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  getMyOvertimeReviewItems: (params = {}, options = {}) =>
    request({ url: ROUTES.businessMyHROvertimeReviewItems, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),
  getScheduleFit: (memberId, params = {}, options = {}) =>
    request({ url: ROUTES.businessHRScheduleFit(memberId), method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),
  getProposedScheduleFit: (memberId, data, options = {}) =>
    request({ url: ROUTES.businessHRScheduleFitProposed(memberId), method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),
  previewScheduleFit: (data, options = {}) =>
    request({ url: ROUTES.businessHRScheduleFitPreview, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- HR: Separation / offboarding (Batch 5) -------- */
  getSeparations: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRSeparations, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),
  getMemberSeparation: (memberId, params = {}, options = {}) =>
    request({ url: ROUTES.businessHRMemberSeparation(memberId), method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),
  recordSeparation: (memberId, data, options = {}) =>
    request({ url: ROUTES.businessHRMemberSeparation(memberId), method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),
  completeSeparation: (id, options = {}) =>
    request({ url: ROUTES.businessHRSeparationAction(id, "complete"), method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  cancelSeparation: (id, data, options = {}) =>
    request({ url: ROUTES.businessHRSeparationAction(id, "cancel"), method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),
  endOnboarding: (memberId, data, options = {}) =>
    request({ url: ROUTES.businessHREndOnboarding(memberId), method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- HR: Statutory foundations (CD-S1) -------- */
  // Government rules Apparify uses. Read only.
  getStatutoryRules: (options = {}) =>
    request({ url: ROUTES.businessHRStatutoryRules, method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  // The signed-in employee's own government numbers; the server resolves the employee.
  getMyGovernmentNumbers: (options = {}) =>
    request({ url: ROUTES.businessHRMyGovernmentNumbers, method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  revealMyGovernmentNumber: (agency) =>
    request({ url: ROUTES.businessHRMyGovernmentNumberReveal, method: "POST",
      data: { agency }, headers: { "X-Portal": "business" } }),

  // An employee's government numbers: masked by default, revealed one at a time on request.
  getEmployeeGovernmentNumbers: (memberId, options = {}) =>
    request({ url: ROUTES.businessHREmployeeGovernmentNumbers(memberId), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  revealEmployeeGovernmentNumber: (memberId, agency) =>
    request({ url: ROUTES.businessHREmployeeGovernmentNumberReveal(memberId), method: "POST",
      data: { agency }, headers: { "X-Portal": "business" } }),
  updateEmployeeGovernmentNumbers: (memberId, changes) =>
    request({ url: ROUTES.businessHREmployeeGovernmentNumbers(memberId), method: "PUT",
      data: changes, headers: { "X-Portal": "business" } }),

  // The business's own registration numbers (owner only).
  getBusinessRegistrations: (options = {}) =>
    request({ url: ROUTES.businessHRBusinessRegistrations, method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  revealBusinessRegistration: (agency) =>
    request({ url: ROUTES.businessHRBusinessRegistrationReveal, method: "POST",
      data: { agency }, headers: { "X-Portal": "business" } }),
  updateBusinessRegistrations: (changes) =>
    request({ url: ROUTES.businessHRBusinessRegistrations, method: "PUT",
      data: changes, headers: { "X-Portal": "business" } }),

  // Statutory facts HR records about an employee. They are not pay.
  getEmployeeStatutoryFacts: (memberId, options = {}) =>
    request({ url: ROUTES.businessHREmployeeStatutoryFacts(memberId), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),
  recordEmployeeStatutoryFact: (memberId, data) =>
    request({ url: ROUTES.businessHREmployeeStatutoryFacts(memberId), method: "POST",
      data, headers: { "X-Portal": "business" } }),
  cancelEmployeeStatutoryFact: (memberId, factId, reason) =>
    request({ url: ROUTES.businessHREmployeeStatutoryFactCancel(memberId, factId), method: "POST",
      data: { reason }, headers: { "X-Portal": "business" } }),

  /** -------- HR: Compensation -------- */
  // My HR > My pay: the signed-in employee's own agreement; the server resolves the employee.
  // My HR > Pay: the signed-in employee's own standing deductions and loan balances (APP-HR-065). Read-only.
  getMyDeductions: (options = {}) =>
    request({ url: ROUTES.businessMyHRDeductions, method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  getMyCompensation: (options = {}) =>
    request({ url: ROUTES.businessHRMyCompensation, method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  getCompensationHistory: (memberId, options = {}) =>
    request({ url: ROUTES.businessHRCompensation(memberId), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  getCurrentCompensation: (memberId, options = {}) =>
    request({ url: ROUTES.businessHRCompensationCurrent(memberId), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  setCompensation: (memberId, data, options = {}) =>
    request({ url: ROUTES.businessHRCompensation(memberId), method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  updateCompensation: (memberId, compId, data, options = {}) =>
    request({ url: ROUTES.businessHRCompensationById(memberId, compId), method: "PATCH",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  cancelCompensationChange: (memberId, compId, options = {}) =>
    request({ url: ROUTES.businessHRCompensationById(memberId, compId), method: "DELETE",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- HR: Settings -------- */
  getHRSettings: (options = {}) =>
    request({ url: ROUTES.businessHRSettings, method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  updateHRSettings: (data, options = {}) =>
    request({ url: ROUTES.businessHRSettings, method: "PATCH",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- Calendar: official holidays (read only) and the business's closure days -------- */
  getCalendarYear: (year, options = {}) =>
    request({ url: ROUTES.businessHRCalendarYear(year), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  createClosureDay: (data, options = {}) =>
    request({ url: ROUTES.businessHRCalendarClosures, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  updateClosureDay: (id, data, options = {}) =>
    request({ url: ROUTES.businessHRCalendarClosureById(id), method: "PATCH",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  removeClosureDay: (id, expectedUpdatedAt, options = {}) =>
    request({ url: ROUTES.businessHRCalendarClosureById(id), method: "DELETE",
      params: expectedUpdatedAt ? { expectedUpdatedAt } : undefined,
      headers: { "X-Portal": "business" }, signal: options.signal }),

  /** How holiday pay is handled for one employee (payroll managers). */
  getHolidayPayHandling: (memberId, options = {}) =>
    request({ url: ROUTES.businessHRHolidayPayHandling(memberId), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  recordHolidayPayHandling: (memberId, data, options = {}) =>
    request({ url: ROUTES.businessHRHolidayPayHandling(memberId), method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- HR: Leave Management -------- */
  getLeaveTypes: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRLeaveTypes, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getLeaveType: (id, options = {}) =>
    request({ url: ROUTES.businessHRLeaveTypeById(id), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  createLeaveType: (data, options = {}) =>
    request({ url: ROUTES.businessHRLeaveTypes, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  // APP-HR-080: delete a leave type nobody has used (the server proves it).
  deleteLeaveType: (id, options = {}) =>
    request({ url: ROUTES.businessHRLeaveTypeById(id), method: "DELETE",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  updateLeaveType: (id, data, options = {}) =>
    request({ url: ROUTES.businessHRLeaveTypeById(id), method: "PATCH",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  getLeaveBalances: (memberId, params = {}, options = {}) =>
    request({ url: ROUTES.businessHRLeaveBalances(memberId), method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  setLeaveBalance: (memberId, typeId, data, options = {}) =>
    request({ url: ROUTES.businessHRLeaveBalance(memberId, typeId), method: "PUT",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  initializeLeaveBalances: (memberId, data = {}, options = {}) =>
    request({ url: ROUTES.businessHRLeaveBalanceInit(memberId), method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  yearEndLeaveCarryOver: (data = {}, options = {}) =>
    request({ url: ROUTES.businessHRLeaveCarryOver, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  getLeaveRequests: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRLeaveRequests, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getLeaveRequest: (id, options = {}) =>
    request({ url: ROUTES.businessHRLeaveRequestById(id), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  submitLeaveRequest: (data, options = {}) =>
    request({ url: ROUTES.businessHRLeaveRequests, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  approveLeaveRequest: (id, data = {}, options = {}) =>
    request({ url: ROUTES.businessHRLeaveRequestById(id) + "/approve", method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  rejectLeaveRequest: (id, data = {}, options = {}) =>
    request({ url: ROUTES.businessHRLeaveRequestById(id) + "/reject", method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  cancelLeaveRequest: (id, data = {}, options = {}) =>
    request({ url: ROUTES.businessHRLeaveRequestById(id) + "/cancel", method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- HR: Deductions -------- */
  getDeductionTypes: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRDeductionTypes, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getDeductionType: (id, options = {}) =>
    request({ url: ROUTES.businessHRDeductionTypeById(id), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  createDeductionType: (data, options = {}) =>
    request({ url: ROUTES.businessHRDeductionTypes, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  // APP-HR-080: delete a deduction type nothing has used (the server proves it).
  deleteDeductionType: (id, options = {}) =>
    request({ url: ROUTES.businessHRDeductionTypeById(id), method: "DELETE",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  updateDeductionType: (id, data, options = {}) =>
    request({ url: ROUTES.businessHRDeductionTypeById(id), method: "PATCH",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  getEmployeeDeductions: (memberId, params = {}, options = {}) =>
    request({ url: ROUTES.businessHREmployeeDeductions(memberId), method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  assignDeduction: (memberId, data, options = {}) =>
    request({ url: ROUTES.businessHREmployeeDeductions(memberId), method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  updateEmployeeDeduction: (deductionId, data, options = {}) =>
    request({ url: ROUTES.businessHREmployeeDeductionById(deductionId), method: "PATCH",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- HR: Payroll -------- */
  getPayrollSchedules: (options = {}) =>
    request({ url: ROUTES.businessHRPayrollSchedules, method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  getPayrollPeriods: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPeriods, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getPayrollPeriod: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPeriodById(id), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  /** Payroll Gate 1: windows of the pay schedule that no payroll period covers (server-derived). */
  getMissingPayrollPeriods: (options = {}) =>
    request({ url: ROUTES.businessHRPayrollMissingPeriods, method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  /** Payroll Gate 2: which pay schedule pays an employee, its history, and the schedules they could be put on. */
  getMemberPaySchedule: (memberId, options = {}) =>
    request({ url: ROUTES.businessHRMemberPaySchedule(memberId), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  /** The dates on which an employee can move to another pay schedule (decided by the server). */
  getPayScheduleTransferOptions: (memberId, toScheduleId, options = {}) =>
    request({ url: ROUTES.businessHRMemberPayTransferOptions(memberId), method: "GET", params: { toScheduleId },
      headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- HR: Pay schedules (Payroll Gate 3) -------- */
  getPaySchedules: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRPaySchedules, method: "GET", params, headers: { "X-Portal": "business" }, signal: options.signal }),
  getPaySchedule: (id, options = {}) =>
    request({ url: ROUTES.businessHRPaySchedule(id), method: "GET", headers: { "X-Portal": "business" }, signal: options.signal }),
  createPaySchedule: (data, options = {}) =>
    request({ url: ROUTES.businessHRPaySchedules, method: "POST", data, headers: { "X-Portal": "business" }, signal: options.signal }),
  updatePaySchedule: (id, data, options = {}) =>
    request({ url: ROUTES.businessHRPaySchedule(id), method: "PATCH", data, headers: { "X-Portal": "business" }, signal: options.signal }),
  deletePaySchedule: (id, options = {}) =>
    request({ url: ROUTES.businessHRPaySchedule(id), method: "DELETE", headers: { "X-Portal": "business" }, signal: options.signal }),
  activatePaySchedule: (id, data = {}, options = {}) =>
    request({ url: `${ROUTES.businessHRPaySchedule(id)}/activate`, method: "POST", data, headers: { "X-Portal": "business" }, signal: options.signal }),
  createPayScheduleSuccessor: (id, data = {}, options = {}) =>
    request({ url: `${ROUTES.businessHRPaySchedule(id)}/successor`, method: "POST", data, headers: { "X-Portal": "business" }, signal: options.signal }),
  archivePaySchedule: (id, data = {}, options = {}) =>
    request({ url: `${ROUTES.businessHRPaySchedule(id)}/archive`, method: "POST", data, headers: { "X-Portal": "business" }, signal: options.signal }),
  restorePaySchedule: (id, data = {}, options = {}) =>
    request({ url: `${ROUTES.businessHRPaySchedule(id)}/restore`, method: "POST", data, headers: { "X-Portal": "business" }, signal: options.signal }),
  makeDefaultPaySchedule: (id, options = {}) =>
    request({ url: `${ROUTES.businessHRPaySchedule(id)}/make-default`, method: "POST", headers: { "X-Portal": "business" }, signal: options.signal }),
  /** Roster: params { state: current|upcoming|ended|all, search, page, limit }. */
  getPayScheduleEmployees: (id, params = {}, options = {}) =>
    request({ url: `${ROUTES.businessHRPaySchedule(id)}/employees`, method: "GET", params, headers: { "X-Portal": "business" }, signal: options.signal }),
  getPayScheduleHistory: (id, params = {}, options = {}) =>
    request({ url: `${ROUTES.businessHRPaySchedule(id)}/history`, method: "GET", params, headers: { "X-Portal": "business" }, signal: options.signal }),
  /** Bulk assign / transfer: { action, scheduleId, memberIds, effectiveFrom | fromFirstPayDay, reason, preview }. */
  bulkPayAssignments: (data, options = {}) =>
    request({ url: ROUTES.businessHRPayAssignmentsBulk, method: "POST", data, headers: { "X-Portal": "business" }, signal: options.signal }),

  /** Employees who have pay but no pay schedule today. */
  getEmployeesWithoutPaySchedule: (options = {}) =>
    request({ url: ROUTES.businessHRPayAssignmentsUnassigned, method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  assignPaySchedule: (data, options = {}) =>
    request({ url: ROUTES.businessHRPayAssignments, method: "POST", data,
      headers: { "X-Portal": "business" }, signal: options.signal }),

  transferPaySchedule: (data, options = {}) =>
    request({ url: ROUTES.businessHRPayAssignmentsTransfer, method: "POST", data,
      headers: { "X-Portal": "business" }, signal: options.signal }),

  cancelPayScheduleAssignment: (assignmentId, data = {}, options = {}) =>
    request({ url: ROUTES.businessHRPayAssignmentCancel(assignmentId), method: "POST", data,
      headers: { "X-Portal": "business" }, signal: options.signal }),

  createPayrollPeriod: (data, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPeriods, method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  updatePayrollPeriod: (id, data, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPeriodById(id), method: "PATCH",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  openPayrollPeriod: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPeriodById(id) + "/open", method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  revertPayrollToDraft: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPeriodById(id) + "/revert-draft", method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  processPayrollPeriod: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPeriodById(id) + "/process", method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  // CD-S2: HR's reviewed amount for a government contribution the rules do not cover.
  recordPayrollStatutoryReview: (id, data, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPeriodById(id) + "/statutory-reviews", method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  // Batch 6A: every employee's payroll facts, warnings and what blocks submission.
  /** The stored holiday decisions of a payroll period, and HR's reviewed amount for one of them. */
  getPayrollHolidayLines: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPeriodById(id) + "/holiday-lines", method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  recordPayrollHolidayReview: (id, data, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPeriodById(id) + "/holiday-reviews", method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  // Report 55: HR's amount for a rest-day or night pay day Apparify could not work out alone.
  recordPayrollPremiumReview: (id, data, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPeriodById(id) + "/premium-reviews", method: "POST",
      data, headers: { "X-Portal": "business" }, signal: options.signal }),

  // Report 57: attendance pay rules (grace, lateness and leaving early), effective-dated; changes are owner-only.
  getHRAttendancePayRules: (options = {}) =>
    request({ url: "/api/business/hr/settings/attendance-pay-rules", method: "GET", headers: { "X-Portal": "business" }, signal: options.signal }),
  recordHRAttendancePayRules: (data, options = {}) =>
    request({ url: "/api/business/hr/settings/attendance-pay-rules", method: "POST", data, headers: { "X-Portal": "business" }, signal: options.signal }),
  withdrawHRAttendancePayRules: (policyId, options = {}) =>
    request({ url: `/api/business/hr/settings/attendance-pay-rules/${policyId}/withdraw`, method: "POST", data: {}, headers: { "X-Portal": "business" }, signal: options.signal }),

  // Report 55: the statutory pay rules registry, the business's establishment facts and employee coverage exceptions.
  getHRPayRules: (options = {}) =>
    request({ url: "/api/business/hr/statutory/pay-rules", method: "GET", headers: { "X-Portal": "business" }, signal: options.signal }),
  recordHREstablishmentFact: (data, options = {}) =>
    request({ url: "/api/business/hr/statutory/pay-rules/establishment", method: "POST", data, headers: { "X-Portal": "business" }, signal: options.signal }),
  cancelHREstablishmentFact: (factId, data, options = {}) =>
    request({ url: `/api/business/hr/statutory/pay-rules/establishment/${factId}/cancel`, method: "POST", data, headers: { "X-Portal": "business" }, signal: options.signal }),
  // Report 58: the business size from Apparify's records (before the owner confirms it), and one employee's pay rules.
  getHRWorkforcePreview: (date, options = {}) =>
    request({ url: "/api/business/hr/statutory/pay-rules/workforce", method: "GET", params: date ? { date } : {}, headers: { "X-Portal": "business" }, signal: options.signal }),
  getHRPayRuleApplicability: (memberId, date, options = {}) =>
    request({ url: `/api/business/hr/statutory/employees/${memberId}/pay-rule-applicability`, method: "GET", params: date ? { date } : {}, headers: { "X-Portal": "business" }, signal: options.signal }),
  // Report 58: the standard week and paid hours of a standard day, effective-dated.
  getHRWorkStandard: (options = {}) =>
    request({ url: "/api/business/hr/settings/work-standard", method: "GET", headers: { "X-Portal": "business" }, signal: options.signal }),
  recordHRWorkStandard: (data, options = {}) =>
    request({ url: "/api/business/hr/settings/work-standard", method: "POST", data, headers: { "X-Portal": "business" }, signal: options.signal }),
  withdrawHRWorkStandard: (standardId, options = {}) =>
    request({ url: `/api/business/hr/settings/work-standard/${standardId}/withdraw`, method: "POST", data: {}, headers: { "X-Portal": "business" }, signal: options.signal }),
  getHRPayRuleCoverage: (memberId, options = {}) =>
    request({ url: `/api/business/hr/statutory/employees/${memberId}/pay-rule-coverage`, method: "GET", headers: { "X-Portal": "business" }, signal: options.signal }),
  proposeHRPayRuleCoverage: (memberId, data, options = {}) =>
    request({ url: `/api/business/hr/statutory/employees/${memberId}/pay-rule-coverage`, method: "POST", data, headers: { "X-Portal": "business" }, signal: options.signal }),
  decideHRPayRuleCoverage: (coverageId, data, options = {}) =>
    request({ url: `/api/business/hr/statutory/pay-rule-coverage/${coverageId}/decide`, method: "POST", data, headers: { "X-Portal": "business" }, signal: options.signal }),
  cancelHRPayRuleCoverage: (coverageId, data, options = {}) =>
    request({ url: `/api/business/hr/statutory/pay-rule-coverage/${coverageId}/cancel`, method: "POST", data, headers: { "X-Portal": "business" }, signal: options.signal }),

  getPayrollInputs: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPeriodById(id) + "/inputs", method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  submitPayrollForReview: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPeriodById(id) + "/submit-review", method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  releasePayrollPeriod: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollRelease(id), method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  cancelPayrollPeriod: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPeriodById(id) + "/cancel", method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  closePayrollPeriod: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPeriodById(id) + "/close", method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  markPayrollPaid: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPeriodById(id) + "/mark-paid", method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  getPayslips: (periodId, params = {}, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPayslips(periodId), method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getPayrollResults: (periodId, params = {}, options = {}) =>
    request({ url: ROUTES.businessHRPayrollResults(periodId), method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getMemberPayrollResults: (memberId, params = {}, options = {}) =>
    request({ url: ROUTES.businessHRPayrollMemberResults(memberId), method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getPayrollResult: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollResultById(id), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  getPayrollResultDetails: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollResultById(id) + "/details", method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  getMemberPayslips: (memberId, params = {}, options = {}) =>
    request({ url: ROUTES.businessHRPayrollMemberPayslips(memberId), method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getPayslip: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPayslipById(id), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  getPayslipDetails: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPayslipById(id) + "/details", method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  getMyPayslips: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRPayrollMyPayslips, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getMyPayslipDetails: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollMyPayslipDetails(id), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  // FIN-2 (PAY-7): the employee confirms receiving their cash salary.
  confirmMySalaryReceipt: (id) =>
    request({ url: ROUTES.businessHRPayrollMyPayslips + `/${id}/confirm-receipt`, method: "POST",
      headers: { "X-Portal": "business" } }),

  voidPayslip: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollPayslipById(id) + "/void", method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  voidPayrollResult: (id, options = {}) =>
    request({ url: ROUTES.businessHRPayrollResultById(id) + "/void", method: "POST",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  getFinancialRecords: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRFinancialRecords, method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getMemberFinancialRecords: (memberId, params = {}, options = {}) =>
    request({ url: ROUTES.businessHRMemberFinancialRecords(memberId), method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getHRAnalytics: (params = {}, options = {}) =>
    request({ url: ROUTES.businessHRAnalytics + "/overview", method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  getHROnboarding: (memberId, options = {}) =>
    request({ url: ROUTES.businessHROnboardingById(memberId), method: "GET",
      headers: { "X-Portal": "business" }, signal: options.signal }),




  /** -------- Invitation Management (from business side) -------- */
  getBusinessInvitations: (params = {}, options = {}) =>
    request({ url: ROUTES.businessMembers + "/invitations", method: "GET",
      params, headers: { "X-Portal": "business" }, signal: options.signal }),

  revokeBusinessInvitation: (invitationId, options = {}) =>
    request({ url: ROUTES.businessMembers + `/invitations/${invitationId}`, method: "DELETE",
      headers: { "X-Portal": "business" }, signal: options.signal }),

  /** -------- Invitations (for invited users) -------- */
  getPendingInvitations: (options = {}) =>
    request({
      url: ROUTES.businessInvitationsPending,
      method: "GET",
      headers: { "X-Portal": "business" },
      signal: options.signal,
    }),


  declineInvitation: (invitationId, options = {}) =>
    request({
      url: ROUTES.businessInvitationDecline(invitationId),
      method: "PATCH",
      headers: { "X-Portal": "business" },
      signal: options.signal,
    }),

  /** -------- Orders -------- */
  getOrders: (options = {}) =>
    request({
      url: ROUTES.businessOrders,
      method: "GET",
      params: options.params,
      headers: { "X-Portal": "business" },
      signal: options.signal,
    }),

  updateOrderStatus: (id, status, options = {}) => {
    if (!id) throw new Error("Request failed: Missing order id");
    return request({
      url: ROUTES.businessOrderStatus(id),
      method: "PATCH",
      data: { status },
      headers: { "X-Portal": "business" },
      signal: options.signal,
    });
  },

  approveReturnRequest: (id, data = {}, options = {}) => {
    if (!id) throw new Error("Request failed: Missing order id");
    return request({
      url: ROUTES.businessOrderReturnRequestApprove(id),
      method: "POST",
      data,
      headers: { "X-Portal": "business" },
      signal: options.signal,
    });
  },

  rejectReturnRequest: (id, data = {}, options = {}) => {
    if (!id) throw new Error("Request failed: Missing order id");
    return request({
      url: ROUTES.businessOrderReturnRequestReject(id),
      method: "POST",
      data,
      headers: { "X-Portal": "business" },
      signal: options.signal,
    });
  },

  refundReturnRequestToWallet: (id, data = {}, options = {}) => {
    if (!id) throw new Error("Request failed: Missing order id");
    return request({
      url: ROUTES.businessOrderReturnRequestRefundWallet(id),
      method: "POST",
      data,
      headers: { "X-Portal": "business" },
      signal: options.signal,
    });
  },

  getOrderActivity: (id, options = {}) => {
    if (!id) throw new Error("Request failed: Missing order id");
    return request({
      url: ROUTES.businessOrders + `/${id}/activity`,
      method: "GET",
      headers: { "X-Portal": "business" },
      signal: options.signal,
    });
  },

  /** -------- Reports -------- */
  getProductSalesReport: (options = {}) =>
    request({
      url: ROUTES.businessProductSales,
      method: "GET",
      headers: { "X-Portal": "business" },
      signal: options.signal,
    }),
};

/** ======================================================================
 *  Compatibility Alias (so old imports won't break immediately)
 *  ======================================================================
 * Your old pages import:  import { adminApi } from "../../services/api";
 * Since you're converting Admin → Business Owner portal,
 * we alias adminApi to businessApi temporarily.
 *
 * Later: rename files + update imports to businessApi.
 */
export const adminApi = {
  getStats: () =>
    request({
      url: ROUTES.adminStats,
    }),

  getPendingBusinesses: (params = {}) =>
    request({
      url: ROUTES.adminBusinessApplications,
      params,
    }),

  getApplicationCounts: () =>
    request({
      url: ROUTES.adminBusinessApplicationCounts,
    }),

  getApplicationDetail: (id) =>
    request({
      url: ROUTES.adminBusinessApplicationById(id),
    }),

  getApplicationHistory: (id) =>
    request({
      url: ROUTES.adminBusinessApplicationHistory(id),
    }),

  getApplicationDocumentUrl: (applicationId, documentId) =>
    getAuthenticatedAssetUrl(ROUTES.adminBusinessApplicationDocument(applicationId, documentId)),

  approveBusiness: (id) =>
    request({
      url: ROUTES.adminBusinessApplicationApprove(id),
      method: "PATCH",
    }),

  rejectBusiness: (id, reason = null) =>
    request({
      url: ROUTES.adminBusinessApplicationReject(id),
      method: "PATCH",
      data: reason ? { reason } : undefined,
    }),

  requestApplicationInfo: (id, message) =>
    request({
      url: ROUTES.adminBusinessApplicationRequestInfo(id),
      method: "POST",
      data: { message },
    }),

  getUsers: () =>
    request({
      url: ROUTES.adminUsers,
    }),

  getBusinesses: () =>
    request({
      url: ROUTES.adminBusinesses,
    }),

  getBusinessDetail: (id) =>
    request({
      url: ROUTES.adminBusinessById(id),
    }),

  moderateBusiness: (id, actionType, reason = null, suspendUntil = null) =>
    request({
      url: ROUTES.adminBusinessModerate(id),
      method: "POST",
      data: { actionType, reason, suspendUntil },
    }),

  getBusinessTimeline: (id) =>
    request({
      url: ROUTES.adminBusinessTimeline(id),
    }),

  getCommissionRevenue: (params = {}) =>
    request({ url: ROUTES.adminCommissionRevenue, params }),

  updateCommissionRate: (ratePercent) =>
    request({
      url: ROUTES.adminCommissionRate,
      method: "PATCH",
      data: { ratePercent },
    }),

  /* ── Appeals ───────────────────────────────── */
  getAppeals: (params = {}) =>
    request({ url: "/api/admin/appeals", params }),

  getAppealCount: () =>
    request({ url: "/api/admin/appeals/count" }),

  getAppealDetail: (id) =>
    request({ url: `/api/admin/appeals/${id}` }),

  resolveAppeal: (id, decision, reviewerNotes = null) =>
    request({
      url: `/api/admin/appeals/${id}/resolve`,
      method: "PATCH",
      data: { decision, reviewerNotes },
    }),

  /* ── Seller Reports ─────────────────────────── */
  getBusinessReports: (params = {}) =>
    request({ url: "/api/admin/reports", params }),

  getBusinessReportCount: () =>
    request({ url: "/api/admin/reports/count" }),

  getBusinessReportDetail: (id) =>
    request({ url: `/api/admin/reports/${id}` }),

  updateBusinessReportStatus: (id, status, reviewerNotes = null) =>
    request({
      url: `/api/admin/reports/${id}/status`,
      method: "PATCH",
      data: { status, reviewerNotes },
    }),
};

/** ======================================================================
 *                           SELLER / STORE API
 * ====================================================================== */

export async function fetchSellerProfile(slug, options = {}) {
  if (!slug) throw new Error("Request failed: Missing seller slug");
  const data = await request({
    url: `/api/sellers/${encodeURIComponent(slug)}`,
    method: "GET",
    signal: options.signal,
  });
  return data;
}

export async function followSeller(slug, options = {}) {
  return request({
    url: `/api/sellers/${encodeURIComponent(slug)}/follow`,
    method: "POST",
    signal: options.signal,
  });
}

export async function unfollowSeller(slug, options = {}) {
  return request({
    url: `/api/sellers/${encodeURIComponent(slug)}/follow`,
    method: "DELETE",
    signal: options.signal,
  });
}

export async function submitSellerReport(slug, payload, options = {}) {
  if (!slug) throw new Error("Request failed: Missing seller slug");

  return request({
    url: `/api/sellers/${encodeURIComponent(slug)}/reports`,
    method: "POST",
    data: payload,
    signal: options.signal,
  });
}

export const sellerApi = {
  fetchSellerProfile,
  followSeller,
  unfollowSeller,
  submitSellerReport,
};

/** ======================================================================
 *  Default Export (kept compatible with existing imports)
 * ====================================================================== */
const api = {
  fetchProducts,
  fetchProductFacets,
  fetchProduct,
  fetchProductReviews,
  submitProductReview,
  likeProductReview,
  unlikeProductReview,
  fetchRecommendations,
  checkReviewEligibility,
  fetchOrder,
  login,
  register,
  verifyEmailOtp,
  resendEmailOtp,
  cartApi,
  orderApi,
  sellerApi,
  applyForBusiness,
  accountBusinessApi,
  businessApi,
  adminApi, // alias
};

export default api;
